import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionOpportunites.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import actualiteService from '../../services/actualiteService';

function GestionActualites() {
  const [actualites, setActualites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    contenu: '',
    image: null,
    categorie: 'Événement',
    date_publication: new Date().toISOString().split('T')[0],
    publie: true
  });

  useEffect(() => {
    loadActualites();
  }, []);

  const loadActualites = async () => {
    try {
      setLoading(true);
      const response = await actualiteService.admin.getAll();
      setActualites(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Log des données avant envoi
      console.log('📤 Données du formulaire:', {
        titre: formData.titre,
        description: formData.description,
        contenu: formData.contenu,
        categorie: formData.categorie,
        date_publication: formData.date_publication,
        publie: formData.publie,
        image: formData.image ? 'Fichier présent' : 'Pas d\'image'
      });
      
      // Ajouter tous les champs obligatoires
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('contenu', formData.contenu);
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('date_publication', formData.date_publication);
      formDataToSend.append('publie', formData.publie ? '1' : '0');
      
      // Ajouter l'image SEULEMENT si elle existe
      if (formData.image && formData.image instanceof File) {
        formDataToSend.append('image', formData.image);
      }

      if (editingId) {
        // Pour la mise à jour, ajouter _method pour Laravel
        formDataToSend.append('_method', 'PUT');
        await actualiteService.admin.update(editingId, formDataToSend);
        alert('Actualité mise à jour avec succès');
      } else {
        await actualiteService.admin.create(formDataToSend);
        alert('Actualité créée avec succès');
      }
      
      setShowModal(false);
      resetForm();
      loadActualites();
    } catch (error) {
      console.error('❌ Erreur complète:', error);
      console.error('📋 Réponse du serveur:', error.response?.data);
      console.error('🔍 Erreurs de validation:', error.response?.data?.errors);
      
      // Afficher les erreurs de validation spécifiques
      if (error.response?.data?.errors) {
        const errorMessages = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n');
        alert(`Erreurs de validation:\n${errorMessages}`);
      } else {
        alert(`Erreur lors de l'enregistrement: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await actualiteService.admin.getById(id);
      const actualite = response.data;
      setFormData({
        titre: actualite.titre,
        description: actualite.description,
        contenu: actualite.contenu,
        image: null,
        categorie: actualite.categorie,
        date_publication: actualite.date_publication,
        publie: actualite.publie
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) return;
    try {
      await actualiteService.admin.delete(id);
      alert('Actualité supprimée avec succès');
      loadActualites();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      contenu: '',
      image: null,
      categorie: 'Événement',
      date_publication: new Date().toISOString().split('T')[0],
      publie: true
    });
    setEditingId(null);
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Actualités" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="page-header">
            <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Nouvelle Actualité
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="opportunites-table">
              <table>
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Date publication</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {actualites.map((actualite) => (
                    <tr key={actualite.id}>
                      <td>{actualite.titre}</td>
                      <td><span className="badge badge-stage">{actualite.categorie}</span></td>
                      <td>{new Date(actualite.date_publication).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <span className={`badge ${actualite.publie ? 'badge-success' : 'badge-draft'}`}>
                          {actualite.publie ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-edit" onClick={() => handleEdit(actualite.id)}>Modifier</button>
                          <button className="btn-delete" onClick={() => handleDelete(actualite.id)}>Supprimer</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingId ? 'Modifier l\'actualité' : 'Nouvelle actualité'}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="opportunite-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titre *</label>
                      <input type="text" value={formData.titre} onChange={(e) => setFormData({ ...formData, titre: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Catégorie *</label>
                      <select value={formData.categorie} onChange={(e) => setFormData({ ...formData, categorie: e.target.value })} required>
                        <option value="Événement">Événement</option>
                        <option value="Résultats">Résultats</option>
                        <option value="Annonce">Annonce</option>
                        <option value="Compétition">Compétition</option>
                        <option value="Vie scolaire">Vie scolaire</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description courte *</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" required />
                  </div>

                  <div className="form-group">
                    <label>Contenu détaillé *</label>
                    <textarea value={formData.contenu} onChange={(e) => setFormData({ ...formData, contenu: e.target.value })} rows="6" required />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Image</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} 
                      />
                    </div>
                    <div className="form-group">
                      <label>Date de publication *</label>
                      <input type="date" value={formData.date_publication} onChange={(e) => setFormData({ ...formData, date_publication: e.target.value })} required />
                    </div>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={formData.publie} onChange={(e) => setFormData({ ...formData, publie: e.target.checked })} />
                      Publier immédiatement
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                    <button type="submit" className="btn-submit">{editingId ? 'Mettre à jour' : 'Créer'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionActualites;